import React from 'react';
import {Box} from '@mui/material';
import {gql} from '@apollo/client';
import {useMutation, useQuery} from '@apollo/client/react';
import dayjs from 'dayjs';
import {toast} from 'react-toastify';
import auth from 'services/auth';
import {ResourceContextProvider} from 'components/record/ResourceContext';
import CreateButton from 'components/forms/CreateButton';
import EventForm from 'components/Event/EventForm';
import {adminTokens} from 'theme/adminTokens';
import {
  ActivityItem,
  ActivityPanel,
  AttentionPanel,
  DashDevice,
  DashEntry,
  DashEvent,
  DeviceHealthPanel,
  EventFilter,
  EventsPanel,
  StatCard,
  StatGrid,
  StatusCounts,
  classifyDevices,
  primaryButtonSx,
  shellButtonSx
} from './DashboardPanels';

const DASHBOARD_EVENTS = gql`
  query DashboardEvents($page: Int, $size: Int, $filters: EventFilters) {
    items: getEvents(page: $page, size: $size, filters: $filters) {
      id
      name
      slug
      status
      startDate
      endDate
      countSubEvents
    }
    total: _getEventsMeta(filters: $filters) {
      count
    }
    counts: _getEventsStatusCounts {
      all
      live
      upcoming
      finished
    }
    live: getEvents(page: 1, size: 1, filters: {status: "live"}) {
      id
      name
      slug
      status
      startDate
      endDate
      countSubEvents
    }
  }
`;

const DASHBOARD_ENTRIES = gql`
  query DashboardEntries {
    items: getEntries(filters: {status: "waiting"}) {
      id
      createdAt
      userName
      subEvent {
        name
        event {
          name
          slug
        }
      }
    }
    total: _getEntriesMeta(filters: {status: "waiting"}) {
      count
    }
  }
`;

const DASHBOARD_DEVICES = gql`
  query DashboardDevices {
    items: getDevices {
      id
      name
      battery
      last
    }
    total: _getDevicesMeta {
      count
    }
  }
`;

const UPDATE_ENTRY = gql`
  mutation DashboardUpdateEntry($payload: EntryPayload!) {
    item: updateEntry(payload: $payload) {
      id
      status
    }
  }
`;

const PAGE_SIZE = 5;

const Dashboard = () => {
  const canEvents = auth.hasPermission('event:manage');
  const canEntries = auth.hasPermission('entry:read');
  const canDevices = auth.hasPermission('device:read');
  const [filter, setFilter] = React.useState<EventFilter>('all');
  const [page, setPage] = React.useState(1);
  const [syncedAt, setSyncedAt] = React.useState<Date | null>(null);
  const [updatingId, setUpdatingId] = React.useState<number | null>(null);

  const filters = filter === 'all' ? {} : {status: filter};
  const eventsQuery = useQuery<{
    items: DashEvent[];
    total: {count: number};
    counts: StatusCounts;
    live: DashEvent[];
  }>(DASHBOARD_EVENTS, {
    variables: {page, size: PAGE_SIZE, filters},
    skip: !canEvents,
    errorPolicy: 'all'
  });
  const entriesQuery = useQuery<{items: DashEntry[]; total: {count: number}}>(DASHBOARD_ENTRIES, {
    skip: !canEntries,
    errorPolicy: 'all'
  });
  const devicesQuery = useQuery<{items: DashDevice[]; total: {count: number}}>(DASHBOARD_DEVICES, {
    skip: !canDevices,
    errorPolicy: 'all'
  });
  const [updateEntry] = useMutation<{item: {id: number; status: string}}>(UPDATE_ENTRY);

  const events = eventsQuery.data?.items || [];
  const counts = eventsQuery.data?.counts;
  const entries = entriesQuery.data?.items || [];
  const devices = devicesQuery.data?.items || [];
  const deviceTotal = devicesQuery.data?.total?.count ?? devices.length;
  const health = classifyDevices(devices);
  const liveEvent = eventsQuery.data?.live?.[0];
  const pendingCount = entriesQuery.data?.total?.count ?? entries.length;

  React.useEffect(() => {
    const busy = eventsQuery.loading || entriesQuery.loading || devicesQuery.loading;
    if (!busy) setSyncedAt(new Date());
  }, [eventsQuery.loading, entriesQuery.loading, devicesQuery.loading, eventsQuery.data, entriesQuery.data, devicesQuery.data]);

  const refresh = () => {
    if (canEvents) void eventsQuery.refetch();
    if (canEntries) void entriesQuery.refetch();
    if (canDevices) void devicesQuery.refetch();
  };

  const decide = async (entry: DashEntry, accept: boolean) => {
    setUpdatingId(entry.id);
    try {
      await updateEntry({variables: {payload: {id: entry.id, status: accept ? 'accept' : 'reject'}}});
      toast.success(`${entry.userName || 'Entry'} ${accept ? 'approved' : 'declined'}`);
      await entriesQuery.refetch();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not update the entry');
    } finally {
      setUpdatingId(null);
    }
  };

  const activity: ActivityItem[] = [
    ...events.slice(0, 3).map((event) => ({
      id: `event-${event.id}`,
      tone: event.status === 'live' ? '#24a67b' : event.status === 'upcoming' ? adminTokens.pink : '#a0aaa3',
      title: event.name,
      detail: event.status === 'live' ? 'is live' : event.status === 'upcoming' ? 'is upcoming' : 'has finished',
      time: dayjs(event.startDate).isValid() ? dayjs(event.startDate).format('D MMM YYYY, HH:mm') : ''
    })),
    ...entries.slice(0, 2).map((entry) => ({
      id: `entry-${entry.id}`,
      tone: adminTokens.pink,
      title: entry.userName || 'New entry',
      detail: `waiting for ${entry.subEvent?.event?.name || 'an event'}`,
      time: entry.createdAt && dayjs(entry.createdAt).isValid() ? dayjs(entry.createdAt).format('D MMM YYYY, HH:mm') : ''
    }))
  ].slice(0, 4);

  const onlineLabel = health.hasTelemetry ? (
    <React.Fragment>
      {health.online} <Box component="span" sx={{fontSize: 14, fontWeight: 400, color: adminTokens.muted, letterSpacing: 0}}>/ {deviceTotal}</Box>
    </React.Fragment>
  ) : (
    deviceTotal
  );

  const now = dayjs();
  const createDefaults = {
    startDate: now.startOf('minute'),
    endDate: now.startOf('minute'),
    publicDate: now.startOf('minute')
  };

  return (
    <Box>
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: {xs: 'flex-start', sm: 'center'}, gap: 2, mb: '27px', flexWrap: 'wrap'}}>
        <Box>
          <Box component="h1" sx={{fontSize: {xs: 27, sm: 31}, letterSpacing: '-1px', margin: '0 0 9px', fontWeight: 700}}>
            Your operations, at a glance.
          </Box>
          <Box component="p" sx={{color: adminTokens.muted, margin: 0, lineHeight: 1.6, fontSize: {xs: 13, sm: 16}}}>
            {now.format('dddd, D MMMM YYYY')}
            <Box component="span" sx={{mx: '8px', color: '#c3cbc5'}}>
              /
            </Box>
            Let’s keep things moving.
          </Box>
        </Box>
        <Box sx={{display: 'flex', gap: '10px'}}>
          <Box
            component="button"
            type="button"
            onClick={refresh}
            sx={{...shellButtonSx, display: {xs: 'none', md: 'inline-flex'}}}
          >
            ↻ Refresh
          </Box>
          {canEvents && (
            <ResourceContextProvider resource="Event">
              <CreateButton label="Create event" defaultValues={createDefaults} callback={refresh} sx={primaryButtonSx}>
                <EventForm />
              </CreateButton>
            </ResourceContextProvider>
          )}
        </Box>
      </Box>

      <StatGrid>
        {canEvents && (
          <StatCard
            label="Total events"
            value={counts?.all ?? '—'}
            foot={
              <React.Fragment>
                <Box component="b" sx={{color: adminTokens.green, fontWeight: 500}}>
                  {counts?.live ?? 0} live
                </Box>
                {' · '}
                {counts?.upcoming ?? 0} upcoming
              </React.Fragment>
            }
          />
        )}
        {canEvents && (
          <StatCard
            label="Live now"
            value={counts?.live ?? '—'}
            foot={liveEvent ? liveEvent.name : 'No event is on course'}
          />
        )}
        {canEntries && <StatCard label="Pending entries" value={pendingCount} foot="Waiting for your approval" />}
        {canDevices && (
          <StatCard
            label="Connected devices"
            value={onlineLabel}
            foot={
              health.hasTelemetry ? (
                <React.Fragment>
                  <Box component="b" sx={{color: adminTokens.green, fontWeight: 500}}>
                    {deviceTotal ? Math.round((health.online / deviceTotal) * 100) : 0}% online
                  </Box>
                  {` · ${health.low} low battery`}
                </React.Fragment>
              ) : (
                'Registered in your workspace'
              )
            }
          />
        )}
      </StatGrid>

      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'minmax(0, 1fr) 315px'}, gap: '23px', '@media (min-width:1600px)': {gridTemplateColumns: 'minmax(0, 1fr) 355px'}}}>
        <Box>
          {canEvents ? (
            <EventsPanel
              events={events}
              total={eventsQuery.data?.total?.count ?? events.length}
              page={page}
              pageSize={PAGE_SIZE}
              counts={counts}
              filter={filter}
              loading={eventsQuery.loading}
              error={!events.length && eventsQuery.error ? 'Events could not be loaded.' : undefined}
              onFilter={(next) => {
                setFilter(next);
                setPage(1);
              }}
              onPage={setPage}
            />
          ) : (
            <Box sx={{background: '#fff', border: `1px solid ${adminTokens.line}`, borderRadius: '11px', padding: '28px', color: adminTokens.muted}}>
              Event management is not enabled for this account.
            </Box>
          )}
          {canEntries && (
            <AttentionPanel
              entries={entries.slice(0, 5)}
              count={pendingCount}
              updatingId={updatingId}
              error={entriesQuery.error ? 'Entries could not be loaded.' : undefined}
              onDecide={decide}
            />
          )}
        </Box>
        <Box sx={{display: 'grid', gap: '22px', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr', md: '1fr'}, alignContent: 'start'}}>
          {canDevices && <DeviceHealthPanel health={{...health, total: deviceTotal}} />}
          <ActivityPanel items={activity} />
        </Box>
      </Box>

      <Box sx={{display: 'flex', justifyContent: 'space-between', color: '#9ca59e', fontSize: 12, mt: '25px', gap: 2}}>
        <span>LIVEZONE ADMIN</span>
        <span>
          <Box component="span" sx={{display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#24a67b', mr: '6px'}} />
          {syncedAt ? `Last synced ${dayjs(syncedAt).format('HH:mm')}` : 'Syncing…'}
        </span>
      </Box>
    </Box>
  );
};

export default Dashboard;
